import { React, useState, useContext } from 'react';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router';
import { AgendaContext } from '../../../contexts/AgendaContext.jsx';
// Mui components
import { Box, Button, Tooltip, Typography } from '@mui/material';
// Components
import ChipEstadoSolicitud from './ChipEstadoSolicitud.jsx';
// Modals
import ModalConfirmarAnularSolcitud from '../Modals/ModalConfirmarAnularSolicitud.jsx';
import ModalMotivoRechazo from '../Modals/ModalMotivoRechazo.jsx';
// Hooks
import { toCamelCaseAllWords, toCamelCaseFirstWord } from '../../../utils/Hooks/textFormatted.js';
// Icons
import iconCircleWarningBlue from '../../../assets/icons/icon_circle-warning-blue.svg';

export default function SolicitudCard({ solicitud }) {
  const { dispatch, motivosRechazo } = useContext(AgendaContext);

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalMotivoRechazoIsVisible, setModalMotivoRechazoIsVisible] = useState(false);

  const handleOpen = () => setModalIsVisible(true);
  const handleClose = () => setModalIsVisible(false);

  const handleOpenModalMotivoRechazo = () => {
    setModalMotivoRechazoIsVisible(!modalMotivoRechazoIsVisible);
  };

  const {
    status,
    nombre,
    direccionOsde,
    direccionExterno,
    proveedor,
    tipoSolicitud,
    id,
    especializacion,
    idMotivoRechazo
  } = solicitud;

  const theme = useTheme();

  const direccionOsdeArray = direccionOsde?.split(',');
  const direccionOsdeFormateada = [...new Set(direccionOsdeArray)].join(', ');

  const direccionExternoArray = direccionExterno?.split(',');
  const direccionExternoFormateada = [...new Set(direccionExternoArray)].join(', ');

  const navigate = useNavigate();

  const handleSolicitudDetail = (route) => {
    dispatch({ type: 'GET_SOLICITUD_DETAIL', payload: solicitud });
    navigate(`${route}`);
  };

  const getDescripcionMotivo = () => {
    return motivosRechazo?.find((mr) => mr?.codMotivoRechazo === idMotivoRechazo)?.descripcion;
  };

  const rechazoConMotivo =
    idMotivoRechazo != null && idMotivoRechazo != undefined && idMotivoRechazo != 6;

  const solicitudParcialmenteAprobada =
    tipoSolicitud === 'MODIFICAR_CONSULTORIO' &&
    status === 'APROBADO' &&
    especializacion.some((especialidad) => especialidad.solicitada && !especialidad.aprobada);

  return (
    <Box>
      <Box
        sx={{
          border: `2px solid ${theme.palette.backgroundSecondary.main}`,
          borderRadius: '1rem',
          borderBottomLeftRadius: solicitudParcialmenteAprobada ? '0' : '1rem',
          borderBottomRightRadius: solicitudParcialmenteAprobada ? '0' : '1rem',
          width: '100%',
          minHeight: solicitudParcialmenteAprobada ? '199px' : '208px',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column'
        }}>
        <ModalConfirmarAnularSolcitud
          modalIsVisible={modalIsVisible}
          solicitud={solicitud}
          handleClose={handleClose}
        />
        <ModalMotivoRechazo
          modalIsVisible={modalMotivoRechazoIsVisible}
          motivoRechazo={motivosRechazo?.find((mr) => mr?.codMotivoRechazo === idMotivoRechazo)}
          handleClose={handleOpenModalMotivoRechazo}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h4">
            {tipoSolicitud === 'ALTA_CONSULTORIO'
              ? 'Solicitud de conexión de agendas'
              : tipoSolicitud === 'BAJA_CONSULTORIO'
              ? 'Solicitud de desconexión de agendas'
              : 'Solicitud de modificación'}
          </Typography>
          {status === 'APROBADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.successBackground.main}
              text="Aprobada"
              colorText={theme.palette.successSecondary.main}
            />
          ) : (status === 'PENDIENTE' || status === 'PLANIFICADO') ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.backgroundSecondary.main}
              text="En análisis"
              colorText={theme.palette.primary.main}
            />
          ) : status === 'RECHAZADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.errorBackground.main}
              text="Rechazada"
              colorText={theme.palette.errorSecondary.main}
            />
          ) : status === 'ANULADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.graySuperLight.main}
              text="Anulada"
              colorText={theme.palette.black.main}
            />
          ) : null}
        </Box>

        <Typography variant="h5" sx={{ color: theme.palette.gray.main, marginTop: '0.25rem' }}>
          {toCamelCaseAllWords(nombre)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
            width: '100%'
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Dirección en {toCamelCaseFirstWord(proveedor)}
            </Typography>
            <Tooltip title={direccionExternoFormateada} placement="bottom-start">
              <Typography
                variant="h5"
                component="p"
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {direccionExternoFormateada}
              </Typography>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Dirección en OSDE
            </Typography>
            <Tooltip title={direccionOsdeFormateada} placement="bottom-start">
              <Typography
                variant="h5"
                component="p"
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {direccionOsdeFormateada}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        {status === 'RECHAZADO' && rechazoConMotivo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '1rem' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Motivo de rechazo
            </Typography>
            <Typography variant="h5" sx={{ display: 'flex' }}>
              {getDescripcionMotivo()}
              <Button
                variant="text"
                disableRipple
                onClick={() => handleOpenModalMotivoRechazo()}
                sx={{
                  color: theme.palette.primary.main,
                  display: 'flex',
                  padding: '0px 0px 0px 1rem',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}>
                <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
                  Ver más
                </Typography>
              </Button>
            </Typography>
          </Box>
        )}

        <Box
          sx={{ width: '100%', marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          {status === 'PENDIENTE' && (
            <Button
              variant="text"
              onClick={() => handleOpen()}
              disableRipple
              sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                borderRight: `2px solid ${theme.palette.backgroundSecondary.main}`,
                padding: '0px 1rem 0px 0px',
                borderRadius: '0px',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}>
              <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
                Anular solicitud
              </Typography>
            </Button>
          )}

          <Button
            variant="text"
            disableRipple
            onClick={() => handleSolicitudDetail(`/prestador/solicitud/${id}`)}
            sx={{
              color: theme.palette.primary.main,
              display: 'flex',
              padding: '0px 0px 0px 1rem',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}>
            <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
              Ver detalle
            </Typography>
          </Button>
        </Box>
      </Box>
      {solicitudParcialmenteAprobada && (
        <Box
          sx={{
            backgroundColor: theme.palette.backgroundSecondary.main,
            borderBottomLeftRadius: '1rem',
            borderBottomRightRadius: '1rem',
            width: '100%',
            minHeight: '48px',
            padding: '8px 24px',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignItems: 'center'
          }}>
          <Box
            component="img"
            sx={{ width: '16px', height: '16px' }}
            alt="img not found"
            src={iconCircleWarningBlue}
          />
          <Typography variant="bodyS" component="p">
            Algunas especialidades no fueron conectadas.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
